import { api, apiFetch } from '@/core/api/client'
import type { Order, OrderItem, OrderStatus } from '@/features/classes/stores/types'

const ordersCollection = api.collection<Order>('orders')
const orderItemsCollection = api.collection<OrderItem>('order-items')

export async function fetchOrders(): Promise<Order[]> {
  return ordersCollection.getFullList({ sort: '-created_at' })
}

export async function fetchOrderById(id: string): Promise<Order | null> {
  try {
    return await ordersCollection.getOne(id)
  } catch {
    return null
  }
}

export async function fetchOrdersByUser(userId: string): Promise<Order[]> {
  const all = await ordersCollection.getFullList({ sort: '-created_at' })
  return all.filter((o) => o.userId === userId)
}

export async function fetchOrdersByCompany(companyId: string): Promise<Order[]> {
  const all = await ordersCollection.getFullList({ sort: '-created_at' })
  return all.filter((o) => o.companyId === companyId)
}

export async function fetchOrderItems(orderId?: string): Promise<OrderItem[]> {
  const all = await orderItemsCollection.getFullList({ sort: '-created_at' })
  if (orderId) {
    return all.filter((i) => i.orderId === orderId)
  }
  return all
}

export async function createOrder(data: {
  userId?: string
  companyId?: string
  status: OrderStatus
  subtotal: number
  taxAmount: number
  total: number
}): Promise<Order> {
  return ordersCollection.create(data)
}

export async function updateOrder(
  id: string,
  data: Partial<{
    status: OrderStatus
    subtotal: number
    taxAmount: number
    total: number
    stripePaymentId: string
    paidAt: string
    notes: string
  }>
): Promise<Order> {
  return ordersCollection.update(id, data)
}

export async function createOrderItem(data: {
  orderId: string
  courseId: string
  quantity: number
  unitPrice: number
  total: number
}): Promise<OrderItem> {
  return orderItemsCollection.create(data)
}

export async function deleteOrder(id: string): Promise<void> {
  await ordersCollection.delete(id)
}

export async function refundOrder(id: string): Promise<{ detail: string; refundId?: string; orderId?: string }> {
  const res = await apiFetch(`/orders/${id}/refund/`, { method: 'POST' })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || `Failed to refund order ${id}`)
  }
  return res.json()
}

export async function cancelOrder(id: string): Promise<{ detail: string; orderId?: string }> {
  const res = await apiFetch(`/orders/${id}/cancel/`, { method: 'POST' })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || `Failed to cancel order ${id}`)
  }
  return res.json()
}

export async function resendOrderConfirmation(
  id: string
): Promise<{ detail: string; orderId?: string; sentTo?: string }> {
  const res = await apiFetch(`/orders/${id}/resend_confirmation/`, { method: 'POST' })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || `Failed to resend confirmation for order ${id}`)
  }
  return res.json()
}

export async function viewOrderReceipt(id: string): Promise<{ receiptUrl: string }> {
  const res = await apiFetch(`/orders/${id}/stripe_receipt/`, { method: 'GET' })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || `Failed to retrieve receipt for order ${id}`)
  }
  return res.json()
}

export async function calculateOrderTotal(
  items: { courseId: string; quantity: number; unitPrice: number }[]
): Promise<{ subtotal: number; taxAmount: number; total: number }> {
  const subtotal = items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0)
  const taxRate = 0.05
  const taxAmount = subtotal * taxRate
  const total = subtotal + taxAmount
  return { subtotal, taxAmount, total }
}
